/**
 * Client-side mirror of `SkTech.Server/Services/CRM/Attachments/CrmAttachmentRules.cs`'s
 * `Validate`, which `ErpAttachmentService.UploadAsync` calls unchanged for ERP transaction
 * attachments (ERP has no rule set of its own - it reuses the CRM one). Keeping this in sync
 * lets the client reject an empty/disallowed/oversize file before any upload request fires,
 * instead of relying on the server's `FileEmpty`/`FileTypeNotAllowed`/`FileTooLarge` response.
 */

export const ERP_MAX_FILE_MB = 25;
export const ERP_MAX_VIDEO_MB = 100;

const ALLOWED_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv', 'rtf', 'odt', 'ods',
  'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg',
  'zip', 'rar', '7z',
  'mp4', 'mov', 'avi', 'mkv', 'webm',
]);

const VIDEO_EXTENSIONS = new Set(['mp4', 'mov', 'avi', 'mkv', 'webm']);

const extensionOf = (name: string) => (name.includes('.') ? name.split('.').pop()!.toLowerCase() : '');

export const isErpVideo = (name: string) => VIDEO_EXTENSIONS.has(extensionOf(name));

/**
 * Returns a locale key (`FileEmpty` | `FileTypeNotAllowed` | `FileTooLarge`) describing why the
 * file is rejected, or `null` when it's acceptable. Mirrors `CrmAttachmentRules.Validate`'s
 * ordering exactly: empty check first, then the extension allowlist, then the size limit (which
 * depends on whether the extension is a video one).
 */
export const validateErpAttachment = (file: File): string | null => {
  if (file.size <= 0) return 'FileEmpty';
  if (!ALLOWED_EXTENSIONS.has(extensionOf(file.name))) return 'FileTypeNotAllowed';
  const limitMb = isErpVideo(file.name) ? ERP_MAX_VIDEO_MB : ERP_MAX_FILE_MB;
  return file.size / 1024 / 1024 > limitMb ? 'FileTooLarge' : null;
};
